import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Phone, Loader2, ShieldCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../utils/apiClient';

const EXCLUDED_PATHS = ['/login', '/register', '/signup', '/verify-phone', '/verify-otp', '/auto-login', '/phone-register', '/terms', '/privacy'];

export default function PhoneRequiredGate() {
  const { user, refreshUser } = useAuth();
  const location = useLocation();
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);

  const excluded = EXCLUDED_PATHS.some(p => location.pathname.startsWith(p));
  const needsPhone = user && !user.phone && !['admin', 'super_admin'].includes(user.role);
  if (!needsPhone || excluded) return null;

  const handleSave = async () => {
    const clean = (phone || '').replace(/[^\d+]/g, '');
    if (clean.replace(/\D/g, '').length < 10) {
      toast.error('Ingresa un número de teléfono válido (ej: +58 412 1234567)');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/api/users/profile`,
        { phone: clean },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('¡Teléfono guardado! Gracias por actualizar tu perfil');
      await refreshUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar el teléfono');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/90 backdrop-blur-sm flex items-center justify-center p-4" data-testid="phone-required-gate">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 space-y-5">
        <div className="flex flex-col items-center text-center space-y-3">
          <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center">
            <Phone className="h-8 w-8 text-amber-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 font-[Outfit]">Necesitamos tu teléfono</h2>
          <p className="text-sm text-slate-600 leading-relaxed">
            Hola{user.name ? ` ${user.name.split(' ')[0]}` : ''}, para continuar usando VíaDirecta necesitamos tu número de teléfono.
            Lo usamos para coordinar tus viajes y contactarte en caso de emergencia.
          </p>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-700">Número de Teléfono / WhatsApp</label>
          <div className="relative">
            <Phone className="absolute left-3 top-3.5 h-5 w-5 text-slate-400" />
            <Input
              type="tel"
              placeholder="+58 412 1234567"
              className="pl-10 h-12"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={saving}
              data-testid="phone-gate-input"
            />
          </div>
        </div>

        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
          data-testid="phone-gate-save-btn"
        >
          {saving ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando...</>
          ) : (
            'Guardar y continuar'
          )}
        </Button>

        <p className="text-xs text-slate-400 text-center flex items-center justify-center gap-1">
          <ShieldCheck className="h-3.5 w-3.5" /> Tu número solo se comparte con tu conductor o pasajero
        </p>
      </div>
    </div>
  );
}
